import { Injectable, TemplateRef, ComponentRef, EmbeddedViewRef } from '@angular/core';
import { Overlay, ComponentType, OverlayConfig, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal, ComponentPortal } from '@angular/cdk/portal';
import { ApDialogContainer } from '../components/dialog-container/dialog-container.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { arrayFlatMap } from '@awp/core/utils';
import { Location } from '@angular/common';
import { ApDialogRef } from './ap-dialog-ref';

const _defaultOptions: OverlayConfig = {
  backdropClass: 'no-backdrop',
  hasBackdrop: true,
  panelClass: ['mat-dialog-container'] // NOTE: This comes from global styles..
}

@Injectable()
export class ApDialogService {

  constructor(
    private _overlay: Overlay,
    private _breakpointObserver: BreakpointObserver,
    private _location: Location) { }

  open<T>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, configBuilder?: (builder: OverlayConfigBuilder) => void): ApDialogRef<T> {

    const builder = new OverlayConfigBuilder(this._overlay, _defaultOptions, this._breakpointObserver);
    if (configBuilder) {
      configBuilder(builder);
    }
    const overlayConfig = new OverlayConfig(builder.getConfig());

    const overlayRef = this._overlay.create(overlayConfig);
    let dialogRef: ApDialogRef<T>;

    const onCloseData: OnCloseData<T> = {};
    const containerPortal = new ComponentPortal(ApDialogContainer);
    const containerPortalRef = overlayRef.attach(containerPortal);
    if (componentOrTemplateRef instanceof TemplateRef) {
      const portal = new TemplatePortal(componentOrTemplateRef, null);
      onCloseData.template = containerPortalRef.instance.attachTemplatePortal(portal);
    } else {
      const portal = new ComponentPortal(componentOrTemplateRef, null);
      onCloseData.component = containerPortalRef.instance.attachComponentPortal(portal);
    }

    dialogRef = new ApDialogRef(onCloseData, overlayRef);
    containerPortalRef.instance.setDialogRef(dialogRef);

    overlayRef.backdropClick().subscribe(() => {
      dialogRef.close();
    });


    // Subscribe to navigation changes - close dialog on navigation.
    const navigationSubscription = this._location.subscribe(() => dialogRef.close());
    overlayRef.detachments().subscribe(() => {
      // Unsubscribe from subscriptions (avoid Memory Leaks here)
      navigationSubscription.unsubscribe();
    });

    return dialogRef;
  }
}

export interface OnCloseData<T> {
  component?: ComponentRef<T>;
  template?: EmbeddedViewRef<T>;
}


/** Helper class to configure an OverlayConfig */
export class OverlayConfigBuilder {

  private _config: OverlayConfig

  constructor(private _overlay: Overlay, config: OverlayConfig, private breakPointObserver: BreakpointObserver) {
    this._config = { ...config };
  }

  /** Makes the dialog fill the screen on a Handset */
  fullScreenOnHandset(): OverlayConfigBuilder {
    if (this.breakPointObserver.isMatched(Breakpoints.Handset)) {
      this._config.width = "100vw";
      this._config.height = "100vh";
      this._config.positionStrategy = this._overlay.position().global().top("0px").left("0px");
      this._config.scrollStrategy = this._overlay.scrollStrategies.block();
    }
    return this;
  }

  /** Makes the overlay bahave like a dropdown over an anchor element */
  dropDownOverAnchor(options: ApDialogDropOptions): OverlayConfigBuilder {
    return this.dropWithflexiblePositions([{
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top'
    }, {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom'
    }], options);
  }

  /** Makes the overlay bahave like a dropdown under an anchor element */
  dropDownUnderAnchor(options: ApDialogDropOptions): OverlayConfigBuilder {
    return this.dropWithflexiblePositions([{
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    }, {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom'
    }], options)
  }

  private dropWithflexiblePositions(positions: ConnectedPosition[], options: ApDialogDropOptions): OverlayConfigBuilder {
    const positionStrategy = this._overlay.position()
      .flexibleConnectedTo(options.anchor)
      .withPositions(positions);

    if (options.offset != null) {
      if (options.offset.y != null) {
        positionStrategy.withDefaultOffsetY(options.offset.y);
      }
      if (options.offset.x != null) {
        positionStrategy.withDefaultOffsetX(options.offset.x);
      }
    }

    this._config.positionStrategy = positionStrategy;
    this._config.scrollStrategy = this._overlay.scrollStrategies.reposition();

    if (options.panelClass != null) {
      this._config.panelClass = arrayFlatMap([this._config.panelClass, options.panelClass]);
    }

    if (options.sameWidthAsAnchor) {
      const rect = options.anchor.getBoundingClientRect();
      this._config.width = rect.width + "px";
    }
    if (options.width) {
      this._config.width = options.width;
    }
    this._config.height = "auto";

    return this;
  }

  /** Sets properties on the OverlayConfig */
  properties(config: OverlayConfig): OverlayConfigBuilder {
    this._config = { ...this._config, ...config };
    return this;
  }

  getConfig(): OverlayConfig {
    return this._config;
  }
}

export interface ApDialogDropOptions {
  /** panel classes to be applied */
  panelClass?: string | string[];
  /** Element to anchor dialog */
  anchor: HTMLElement;
  /** Makes the dropdown panel the same width as the anchor element */
  sameWidthAsAnchor?: boolean;
  /** Custom width */
  width?: string | number;
  /** Offset from anchor */
  offset?: { x?: number, y?: number };
}

